import * as React from 'react';
import { useCallback, useMemo, useRef, Dispatch, useLayoutEffect, useState } from 'react';
import { googleMapsApiKey } from '../config';
import { GoogleMap, MarkerClusterer, Marker, useGoogleMap, useLoadScript } from '@react-google-maps/api';
import { CircularProgress, createStyles, makeStyles } from '@material-ui/core';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import useWindowHeight from '../hooks/useWindowHeight';
import CloseIcon from '@material-ui/icons/Close';
import { JobSummaryAction } from './job-search/JobSummary';
import { SearchItemAction } from '../actions/SearchItemAction';

export interface SearchMapProps {
    markers: Array<{key: string; position: {lat: number; lng: number; }}>;
    center: {lat: number; lng: number};
    bounds?: {north: number; south: number; east: number; west: number; };
    region?: string;
    dispatch: Dispatch<JobSummaryAction | SearchItemAction>;
    selectedEl: React.MutableRefObject<HTMLDivElement | null>;
}

const libraries = ['geometry', 'drawing', 'places'];

const useStyles = makeStyles(
    createStyles({
        root: {
            transition: 'opacity 500ms ease-in',
            opacity: 0,
            zIndex: -1,

            'body.with-map &': {
                transition: 'opacity 1000ms ease-out',
                opacity: 1,
                zIndex: 0
            }
        }
    })
);

export default function SearchMap(props: SearchMapProps) {
    const el = useRef<HTMLDivElement>(null);
    let height = useWindowHeight(el.current && el.current.parentElement);
    const [deltaHeight, setDeltaHeight] = useState(0);
    const classes = useStyles();

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: googleMapsApiKey,
        libraries: libraries,
        region: props.region
    });

    if (loadError) {
        console.error('map not loaded');
        return <div>Map cannot be loaded right now, sorry.</div>;
    }

    useLayoutEffect(() => {
        function onResize() {
            // console.info('onResize:', window.innerWidth, props.selectedEl.current && props.selectedEl.current.offsetHeight);
            if (window.innerWidth <= 600 && props.selectedEl.current) {
                setDeltaHeight(props.selectedEl.current!.offsetHeight);
                // props.selectedEl.current.style.top = (height - deltaHeight) + 'px';
            } else {
                setDeltaHeight(0);
            }
        }
        onResize();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [props.selectedEl.current]);

    // function onLoad(map: google.maps.Map) {
    // }

    function onClose() {
        props.dispatch({type: 'show-on-map', payload: {listing: null as any}});
    }

    const closeButton = useMemo(() => (
        // google.maps.ControlPosition.TOP_RIGHT
        <MapControl position={3} title="Close the map" onClick={onClose}>
            <CloseIcon/>
        </MapControl>), []);

    // tslint:disable-next-line:no-shadowed-variable
    const renderMap = useCallback(() => {
        // console.info('rendering map', props);
        return (
            <GoogleMap
                zoom={11}
                center={props.center}
                mapContainerStyle={{width: '100%', height: height - deltaHeight}}
                mapContainerClassName={classes.root}
                // onLoad={onLoad}
            >
                {closeButton}
                {/*<Polygon {...props.polygon}/>*/}
                <MarkerClusterer
                    options={{imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m'}}
                >
                    {
                        clusterer => props.markers.map(marker => (
                            <Marker
                                key={marker.key}
                                clusterer={clusterer}
                                onClick={() => {
                                    props.dispatch({type: 'focus-item', payload: {id: marker.key}});
                                }}
                                {...marker}
                            />
                        ))
                    }
                </MarkerClusterer>
            </GoogleMap>
        );
    }, [height, deltaHeight, props.center, props.markers]);

    // console.info('render SearchMap');
    // style={{position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, zIndex: 0}}
    return <div id="SearchMap" ref={el} >{isLoaded ? renderMap() : <CircularProgress/>}</div>;
}


function MapControl({position = google.maps.ControlPosition.TOP_LEFT, children, ...otherProps}: any) {
    const map = useGoogleMap();
    const controlDiv = document.createElement('div');

    // @ts-ignore
    useEffect(() => {
        if (map) {
            const controls = map.controls[position];
            const index = controls.getLength();
            controls.push(controlDiv);
            return () => {
                controls.removeAt(index);
            };
        }
    }, [map]);

    return createPortal(
        <button
            className="gm-control-active"
            style={{
                margin: 10,
                height: 40,
                width: 40,
                border: 0,
                borderRadius: 2,
                background: 'none rgb(255, 255, 255)',
                boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
                cursor: 'pointer',
                userSelect: 'none',
            }}
            {...otherProps}
        >
            {children}
        </button>,
        controlDiv
    );
}
